// @tennis-matches/services/winterdoppel/participation

export const WINTERDOPPEL_PARTICIPATION_LIMIT_TYPES = [
  "none",
  "season",
  "month",
  "weeks",
];

export const WINTERDOPPEL_MAYBE_DEFAULT_LIMIT = { type: "month", value: 1 };

/** @returns {{ type: string, value: number } | null} */
export function resolveParticipationLimit(player) {
  if (!player || player.interest_status === "no") return null;
  let type = String(
    player.participation_limit_type || player.participationLimitType || "none",
  );
  let value = Math.floor(
    Number(
      player.participation_limit_value ?? player.participationLimitValue,
    ) || 0,
  );
  if (type === "none" && player.interest_status === "maybe") {
    return { ...WINTERDOPPEL_MAYBE_DEFAULT_LIMIT };
  }
  if (type === "none" || value <= 0) return null;
  return { type, value };
}

export function createParticipationState(initialStarterCounts = new Map()) {
  return {
    starterCounts: new Map(initialStarterCounts),
    monthCounts: new Map(),
    lastDate: new Map(),
  };
}

function __day(isoDate) {
  return String(isoDate || "").slice(0, 10);
}

/** Obergrenze Starter-Einsätze für Fairness-Quote (Planungsziel). */
export function participationCapForFairness(player, sessionDates = []) {
  const limit = resolveParticipationLimit(player);
  if (!limit) return null;
  const dates = (sessionDates || []).map(__day).filter(Boolean);
  if (!dates.length) return limit.type === "season" ? limit.value : null;

  if (limit.type === "season") {
    return Math.min(limit.value, dates.length);
  }
  if (limit.type === "month") {
    const months = new Set(dates.map((d) => d.slice(0, 7))).size;
    return months * limit.value;
  }
  if (limit.type === "weeks") {
    return Math.ceil(dates.length / limit.value);
  }
  return null;
}

export function effectiveFairnessDenominator(verfuegbar, cap) {
  const v = Math.max(0, Math.floor(Number(verfuegbar) || 0));
  if (cap == null || cap <= 0) return v;
  return Math.min(v, cap);
}

export function participationSlotAllowed(
  player,
  sessionDate,
  state,
  sessionDates = [],
) {
  const limit = resolveParticipationLimit(player);
  if (!limit || !player?.id) return true;

  const pid = String(player.id);
  const day = __day(sessionDate);
  const count = state.starterCounts.get(pid) || 0;

  if (limit.type === "season") {
    return count < limit.value;
  }

  if (limit.type === "month") {
    if (!day) return false;
    const monthKey = `${pid}:${day.slice(0, 7)}`;
    return (state.monthCounts.get(monthKey) || 0) < limit.value;
  }

  if (limit.type === "weeks") {
    const dates = (sessionDates || []).map(__day).filter(Boolean);
    const maxTotal = Math.ceil(dates.length / limit.value);
    if (count >= maxTotal) return false;
    const last = state.lastDate.get(pid);
    if (!last) return true;
    const diff =
      (Date.parse(`${day}T12:00:00`) - Date.parse(`${last}T12:00:00`)) /
      86400000;
    return diff >= limit.value * 7;
  }

  return true;
}

export function recordParticipationAssignment(playerId, sessionDate, state) {
  const pid = String(playerId || "");
  const day = __day(sessionDate);
  if (!pid || !day) return;
  state.starterCounts.set(pid, (state.starterCounts.get(pid) || 0) + 1);
  const monthKey = `${pid}:${day.slice(0, 7)}`;
  state.monthCounts.set(monthKey, (state.monthCounts.get(monthKey) || 0) + 1);
  state.lastDate.set(pid, day);
}

export function seedParticipationStateFromAssignments(
  sessionPlayers = [],
  sessionsById = new Map(),
) {
  const starterCounts = new Map();
  const monthCounts = new Map();
  const lastDate = new Map();

  const sorted = (sessionPlayers || [])
    .filter((sp) => sp?.role === "starter" && sp.rsvp !== "no")
    .map((sp) => {
      const session = sessionsById.get(String(sp.session_id || ""));
      return {
        playerId: String(sp.player_id || ""),
        day: __day(session?.session_date),
      };
    })
    .filter((r) => r.playerId && r.day)
    .sort((a, b) => a.day.localeCompare(b.day));

  for (const { playerId, day } of sorted) {
    recordParticipationAssignment(playerId, day, {
      starterCounts,
      monthCounts,
      lastDate,
    });
  }

  return { starterCounts, monthCounts, lastDate };
}

/** i18n-Key + params für Spielerliste. */
export function participationLimitDisplay(player) {
  const storedType = String(
    player?.participation_limit_type ||
      player?.participationLimitType ||
      "none",
  );
  const limit = resolveParticipationLimit(player);
  if (player?.interest_status === "maybe" && storedType === "none") {
    return { key: "winterdoppel.participation.list.maybeDefault", params: {} };
  }
  if (!limit) {
    return { key: "winterdoppel.participation.list.none", params: {} };
  }
  return {
    key: `winterdoppel.participation.list.${limit.type}`,
    params: { count: limit.value },
  };
}
